import { useCallback } from "react";
import { AxiosError } from "axios";
import { useActionRequest, useDataRequest } from "../../common/hooks/api";
import { NetworkRequestState } from "../../common/models";
import { Api } from "../../common/service/Api";
import {
  PostmarkTestSendRequest,
  UpdateOrganizationPostmarkMailbox,
  UpdateOrganizationPostmarkMailboxShare,
} from "../../common/service/server/controller/OrganizationController";
import {
  OrganizationPostmarkMailboxDto,
  OrganizationPostmarkMailboxShareDto,
  OrganizationPostmarkMailboxShareOptionDto,
  PostmarkTestSendResponse,
} from "../../common/service/server/dto/OrganizationDto";
import { OrganizationPublicId } from "../../common/service/server/Types";
import { POSTMARK_INACTIVE_RECIPIENT_MESSAGE } from "../util/privacyEmailSetup";

export const usePostmarkMailbox = (
  organizationId: OrganizationPublicId | undefined,
): {
  mailbox: OrganizationPostmarkMailboxDto | null | undefined;
  request: NetworkRequestState;
  refresh: () => Promise<OrganizationPostmarkMailboxDto | null | undefined>;
  upsert: (payload: UpdateOrganizationPostmarkMailbox) => Promise<void>;
  upsertRequest: NetworkRequestState;
  verify: () => Promise<OrganizationPostmarkMailboxDto>;
  verifyRequest: NetworkRequestState;
  sendTest: (payload: PostmarkTestSendRequest) => Promise<PostmarkTestSendResponse>;
  sendTestRequest: NetworkRequestState;
  share: OrganizationPostmarkMailboxShareDto | null | undefined;
  shareOptions: OrganizationPostmarkMailboxShareOptionDto[] | undefined;
  refreshShare: () => Promise<OrganizationPostmarkMailboxShareDto | null | undefined>;
  refreshShareOptions: () => Promise<OrganizationPostmarkMailboxShareOptionDto[] | undefined>;
  upsertShare: (payload: UpdateOrganizationPostmarkMailboxShare) => Promise<void>;
  upsertShareRequest: NetworkRequestState;
  deleteShare: () => Promise<void>;
  deleteShareRequest: NetworkRequestState;
} => {
  const {
    result: mailbox,
    request,
    refresh,
  } = useDataRequest<OrganizationPostmarkMailboxDto | null>({
    queryKey: ["postmarkMailbox", organizationId],
    api: () => Api.organization.getOrganizationPostmarkMailbox(organizationId),
    notReady: !organizationId,
  });

  const {
    result: share,
    request: shareRequest,
    refresh: refreshShareBase,
  } = useDataRequest<OrganizationPostmarkMailboxShareDto | null>({
    queryKey: ["postmarkMailboxShare", organizationId],
    api: () => Api.organization.getOrganizationPostmarkMailboxShare(organizationId),
    notReady: !organizationId,
  });

  const {
    result: shareOptions,
    request: shareOptionsRequest,
    refresh: refreshShareOptionsBase,
  } = useDataRequest<OrganizationPostmarkMailboxShareOptionDto[]>({
    queryKey: ["postmarkMailboxShareOptions", organizationId],
    api: () => Api.organization.getOrganizationPostmarkMailboxShareOptions(organizationId),
    notReady: !organizationId,
  });

  const refreshMailbox = useCallback(async () => {
    const typedRefresh = refresh as unknown as () => Promise<
      OrganizationPostmarkMailboxDto | null | undefined
    >;
    return await typedRefresh();
  }, [refresh]);

  const refreshShare = useCallback(async () => {
    const typedRefresh = refreshShareBase as unknown as () => Promise<
      OrganizationPostmarkMailboxShareDto | null | undefined
    >;
    return await typedRefresh();
  }, [refreshShareBase]);

  const refreshShareOptions = useCallback(async () => {
    const typedRefresh = refreshShareOptionsBase as unknown as () => Promise<
      OrganizationPostmarkMailboxShareOptionDto[] | undefined
    >;
    return await typedRefresh();
  }, [refreshShareOptionsBase]);

  const setContextualError = useCallback(
    (networkRequestState: NetworkRequestState, error: Error | AxiosError) => {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>;
      const response = axiosError?.response?.data;
      const responseMessage = response?.message?.trim();
      const responseError = response?.error?.trim();
      const status = axiosError?.response?.status;

      if (responseMessage) {
        networkRequestState.errorMessage = responseMessage;
        return;
      }

      if (status === 409 && (!responseMessage || responseError === "Conflict")) {
        networkRequestState.errorMessage =
          "This domain is in use by another organization. Please use another domain.";
        return;
      }

      networkRequestState.errorMessage =
        responseError ?? error?.message ?? "There was an error while processing your request.";
    },
    [],
  );

  const setSendTestError = useCallback(
    (networkRequestState: NetworkRequestState, error: Error | AxiosError) => {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 422) {
        networkRequestState.errorMessage = POSTMARK_INACTIVE_RECIPIENT_MESSAGE;
        return;
      }
      setContextualError(networkRequestState, error);
    },
    [setContextualError],
  );

  const { fetch: upsert, request: upsertRequest } = useActionRequest<
    OrganizationPostmarkMailboxDto,
    UpdateOrganizationPostmarkMailbox
  >({
    api: (payload) => Api.organization.upsertOrganizationPostmarkMailbox(organizationId, payload),
    onSuccess: async () => {
      await refresh();
    },
    onError: setContextualError,
    notReady: !organizationId,
  });

  const { fetchWithResult: verify, request: verifyRequest } =
    useActionRequest<OrganizationPostmarkMailboxDto>({
      api: () => Api.organization.verifyOrganizationPostmarkMailbox(organizationId),
      onSuccess: async () => {
        await refresh();
      },
      onError: setContextualError,
      notReady: !organizationId,
    });

  const { fetchWithResult: sendTest, request: sendTestRequest } = useActionRequest<
    PostmarkTestSendResponse,
    PostmarkTestSendRequest
  >({
    api: (payload) => Api.organization.sendPostmarkTestEmail(organizationId, payload),
    onSuccess: async () => {
      await refresh();
    },
    onError: setSendTestError,
    notReady: !organizationId,
  });

  const { fetch: upsertShare, request: upsertShareRequest } = useActionRequest<
    OrganizationPostmarkMailboxShareDto,
    UpdateOrganizationPostmarkMailboxShare
  >({
    api: (payload) =>
      Api.organization.upsertOrganizationPostmarkMailboxShare(organizationId, payload),
    onSuccess: async () => {
      await refresh();
      await refreshShareBase();
      await refreshShareOptionsBase();
    },
    onError: setContextualError,
    notReady: !organizationId,
  });

  const { fetch: deleteShare, request: deleteShareRequest } = useActionRequest<void>({
    api: () => Api.organization.deleteOrganizationPostmarkMailboxShare(organizationId),
    onSuccess: async () => {
      await refreshShareBase();
      await refreshShareOptionsBase();
    },
    onError: setContextualError,
    notReady: !organizationId,
  });

  return {
    mailbox: mailbox ?? null,
    request: request?.running
      ? request
      : shareRequest?.running
      ? shareRequest
      : shareOptionsRequest?.running
      ? shareOptionsRequest
      : request,
    refresh: refreshMailbox,
    upsert,
    upsertRequest,
    verify,
    verifyRequest,
    sendTest,
    sendTestRequest,
    share: share ?? null,
    shareOptions,
    refreshShare,
    refreshShareOptions,
    upsertShare,
    upsertShareRequest,
    deleteShare,
    deleteShareRequest,
  };
};
